/**
 * RadarPolygon — gráfico radar (teia) SVG puro.
 *
 * Renderiza N vértices reais (não força 6). Para N=4 (Visibilidade,
 * Engajamento, Tráfego, Vendas) gera polígono de 4 vértices.
 *
 * Layout: viewBox 560×530, centro cx=280 cy=265, raio máximo R=200.
 * Eixos distribuídos igualmente de -90° em sentido horário.
 *
 * Cores e gradientes: apenas tokens CSS (zero hardcode).
 * Acessibilidade: role="img" + aria-label descritivo.
 * Reduced-motion: sem animação CSS embutida — respeitado via tokens.
 */

interface RadarDimension {
  readonly key: string;
  readonly label: string;
  readonly score: number; // 0–100
}

interface RadarPolygonProps {
  readonly dimensions: readonly RadarDimension[];
  readonly onDimClick?: (key: string) => void;
}

const CX = 280;
const CY = 265;
const R_MAX = 200;
const RINGS = [40, 80, 120, 160, 200]; // 20,40,60,80,100 %
const SCALE_LABELS = [20, 40, 60, 80, 100];
const LABEL_OFFSET = 32; // px além do anel externo

/** Converte ângulo (graus) + raio para coordenadas cartesianas */
function polar(r: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

/** Gera os pontos do polígono de N vértices com raios normalizados */
function buildPolygonPoints(dims: readonly RadarDimension[], rScale: number): string {
  const n = dims.length;
  return dims
    .map((d, i) => {
      const angle = -90 + i * (360 / n);
      const r = (d.score / 100) * rScale;
      const [x, y] = polar(r, angle);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/** Gera pontos de um anel regular de N lados */
function buildRingPoints(n: number, r: number): string {
  return Array.from({ length: n }, (_, i) => {
    const angle = -90 + i * (360 / n);
    const [x, y] = polar(r, angle);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

/** Escolhe a cor do rótulo da dimensão conforme score */
function labelColor(score: number): string {
  if (score >= 75) return 'var(--md-primary)';
  if (score >= 55) return '#0079DC';
  return '#C0392B';
}

/** Âncora de texto e deslocamento do rótulo externo */
function labelAnchorProps(
  angleDeg: number,
  labelR: number,
): {
  x: number;
  y: number;
  textAnchor: string;
} {
  const [x, y] = polar(labelR, angleDeg);
  // Lateral esquerda = end, lateral direita = start, topo/base = middle
  const normalized = ((angleDeg % 360) + 360) % 360;
  let textAnchor = 'middle';
  if (normalized > 30 && normalized < 150) textAnchor = 'start';
  else if (normalized > 210 && normalized < 330) textAnchor = 'end';
  return { x, y, textAnchor: textAnchor as 'middle' | 'start' | 'end' };
}

export function RadarPolygon({ dimensions, onDimClick }: RadarPolygonProps): JSX.Element {
  const n = dimensions.length;
  const ariaLabel = dimensions.map((d) => `${d.label} ${d.score}`).join(', ');

  // Gradiente fill ID único (evita colisão se múltiplos radars na página)
  const fillId = 'rfg-poly';
  const strokeId = 'rsg-poly';

  return (
    <svg
      viewBox="0 0 560 530"
      width="100%"
      role="img"
      aria-label={`Radar das ${n} dimensoes: ${ariaLabel}`}
      focusable="false"
      style={{ display: 'block' }}
    >
      <defs>
        {/* Preenchimento tricolor gradiente da marca */}
        <linearGradient id={fillId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3F2570" stopOpacity="0.38" />
          <stop offset="50%" stopColor="#0079DC" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#F9005C" stopOpacity="0.22" />
        </linearGradient>
        {/* Borda tricolor gradiente da marca */}
        <linearGradient id={strokeId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3F2570" />
          <stop offset="50%" stopColor="#0079DC" />
          <stop offset="100%" stopColor="#F9005C" />
        </linearGradient>
      </defs>

      {/* Grade: anéis + eixos */}
      <g
        stroke="var(--md-outline-variant)"
        fill="none"
        aria-hidden="true"
        className="radar-grid-anim"
      >
        {RINGS.map((r, ri) => (
          <polygon
            key={r}
            points={buildRingPoints(n, r)}
            strokeWidth={ri >= 3 ? 1.2 : 1}
            opacity={0.55 - ri * 0.05}
          />
        ))}
        {/* Eixos do centro aos vértices externos */}
        {dimensions.map((_, i) => {
          const angle = -90 + i * (360 / n);
          const [ex, ey] = polar(R_MAX, angle);
          return (
            <line
              key={i}
              x1={CX}
              y1={CY}
              x2={ex.toFixed(1)}
              y2={ey.toFixed(1)}
              strokeWidth={1}
              opacity={0.4}
            />
          );
        })}
      </g>

      {/* Escala numérica no eixo do primeiro vértice (θ = -90°) */}
      <g
        fontFamily="'Roboto Flex', sans-serif"
        fontSize="8.5"
        fill="var(--md-on-surface-variant)"
        textAnchor="start"
        aria-hidden="true"
        className="radar-grid-anim"
      >
        {RINGS.map((r, ri) => {
          const [, y] = polar(r, -90);
          return (
            <text key={r} x={CX + 7} y={(y + 3).toFixed(1)}>
              {SCALE_LABELS[ri]}
            </text>
          );
        })}
      </g>

      {/* Polígono de dados */}
      <g className="radar-polygon-anim" aria-hidden="true">
        {/* Preenchimento */}
        <polygon
          points={buildPolygonPoints(dimensions, R_MAX)}
          fill={`url(#${fillId})`}
          stroke="none"
        />
        {/* Borda */}
        <polygon
          points={buildPolygonPoints(dimensions, R_MAX)}
          fill="none"
          stroke={`url(#${strokeId})`}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />

        {/* Vértices clicáveis */}
        {dimensions.map((d, i) => {
          const angle = -90 + i * (360 / n);
          const r = (d.score / 100) * R_MAX;
          const [vx, vy] = polar(r, angle);
          // Cor do dot: interpola entre purple/blue/pink conforme posição
          const dotColor = i % 3 === 0 ? '#3F2570' : i % 3 === 1 ? '#0079DC' : '#F9005C';
          return (
            <g
              key={d.key}
              className="radar-dot-btn"
              role="button"
              tabIndex={0}
              aria-label={`${d.label}, ${d.score}, abrir detalhes`}
              style={{ cursor: 'pointer' }}
              onClick={() => onDimClick?.(d.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDimClick?.(d.key);
                }
              }}
            >
              <circle cx={vx.toFixed(1)} cy={vy.toFixed(1)} r={20} fill="transparent" />
              <circle
                className="radar-dot-vis"
                cx={vx.toFixed(1)}
                cy={vy.toFixed(1)}
                r={6}
                fill={dotColor}
                stroke="#fff"
                strokeWidth="2.5"
              />
            </g>
          );
        })}
      </g>

      {/* Rótulos externos */}
      <g className="radar-labels-anim">
        {dimensions.map((d, i) => {
          const angle = -90 + i * (360 / n);
          const labelR = R_MAX + LABEL_OFFSET;
          const { x, y, textAnchor } = labelAnchorProps(angle, labelR);
          const color = labelColor(d.score);
          return (
            <g
              key={d.key}
              className="radar-label-btn"
              role="button"
              tabIndex={0}
              aria-label={`${d.label}, ${d.score}, abrir detalhes`}
              style={{ cursor: 'pointer' }}
              onClick={() => onDimClick?.(d.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDimClick?.(d.key);
                }
              }}
            >
              {/* Área de toque invisível */}
              <rect
                x={textAnchor === 'middle' ? x - 60 : textAnchor === 'start' ? x - 4 : x - 116}
                y={y - 14}
                width={120}
                height={34}
                rx={4}
                fill="transparent"
              />
              <text
                x={x.toFixed(1)}
                y={(y - 2).toFixed(1)}
                fontFamily="'Space Grotesk', sans-serif"
                fontSize="11"
                fontWeight="700"
                fill={color}
                textDecoration="underline"
              >
                {d.label.toUpperCase()}
              </text>
              <text
                x={x.toFixed(1)}
                y={(y + 12).toFixed(1)}
                fontFamily="'Space Grotesk', sans-serif"
                fontSize="18"
                fontWeight="700"
                fill={d.score < 60 ? '#C0392B' : 'var(--md-on-surface)'}
              >
                {d.score}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
